import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import Divider from '@mui/material/Divider'
import { format } from 'date-fns'
import { usePermissions } from "@/hooks/usePermissions"
import {
  useDeleteWhiteListMutation,
  useGetWhiteListByBuildingIdQuery,
} from '@/api/whiteList'
import { useDispatch, useSelector } from 'react-redux'
import { selectActiveBuildingId } from '@/features/building/buildingSlice'
import WidgetPaper from '@/components/WidgetPaper'
import ImportExportButton from './ImportExportButton';
import SearchInput from "@/components/SearchInput";
import BlockIcon from '@mui/icons-material/Block';
import HowToRegTwoToneIcon from '@mui/icons-material/HowToRegTwoTone';
import HourglassTopTwoToneIcon from '@mui/icons-material/HourglassTopTwoTone';
import AddButton from './AddButton'
import AccessTypeSelector from './AccessTypeSelector'
import TablePageSelector from './Table/TablePageSelector'
import TableHeader, { accessTableHeaderItems } from './Table/TableHeader'
import TableRowsSkeleton from '@/components/TableRowsSkeleton'
import TableEmptyPlaceholder from '@/components/TableEmptyPlaceholder'
import { 
  setUpsertModalParams,
  normalizeAccessType,
  resolveUiAccessType,
  useAllowedAccessTypes,
} from '@/features/access'

const hasAbonementLink = (entry) => {
  const candidates = [
    entry?.abonement_id,
    entry?.abonement?.id,
    entry?.abonementId,
    entry?.subscription_id,
    entry?.subscription?.id,
    entry?.subscriptionId
  ]

  const hasLinkedAbonement = candidates.some((candidate) => {
    if (candidate == null || candidate === '') return false
    const parsed = Number(candidate)
    return Number.isFinite(parsed) && parsed > 0
  })

  if (hasLinkedAbonement) return true

  const normalizedType = String(entry?.access_type || '').trim().toLowerCase()
  return normalizedType === 'abonement' || normalizedType === 'subscription'
}

const filtersList = [
  {
    value: 'guestList',
    accessType: 'guest' as const,
    icon: <HourglassTopTwoToneIcon fontSize="small" sx={{ mr: 1 }}/>,
    translationKey: "whitelist-tabs_guest"
  },
  {
    value: 'whiteList',
    accessType: 'white' as const,
    icon: <HowToRegTwoToneIcon fontSize="small" sx={{ mr: 1 }}/>,
    translationKey: "whitelist-tabs_whitelist"
  },
  {
    value: 'blackList',
    accessType: 'black' as const,
    icon: <BlockIcon fontSize="small" sx={{ mr: 1 }}/>,
    translationKey: "whitelist-tabs_blacklist"
  },
  // {
  //   value: 'other',
  //   translationKey: "other"
  // }
]

const Access = ({ ref }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation()
  const activeBuildingId = useSelector(selectActiveBuildingId);
  const { hasPermission } = usePermissions()
  const { isAccessTypeAllowed } = useAllowedAccessTypes()
  const allowedFiltersList = filtersList.filter((filter) => isAccessTypeAllowed(filter.accessType))
  const [preferredAccessType, setSelectedAccessType] = useState(filtersList[0].value);
  const selectedAccessType = allowedFiltersList.some((filter) => filter.value === preferredAccessType)
    ? preferredAccessType
    : allowedFiltersList[0]?.value ?? ''
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const [prefetchSize, setPrefetchSize] = useState(100)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [currentId, setCurrentId] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  // used for this page and upsert modal
  const requestParams = {
    buildingId: activeBuildingId,
    page: 1,
    plate_number: search,
    size: prefetchSize
  }
  const [ handleDelete, { 
    isSuccess: successDelete 
  }] = useDeleteWhiteListMutation();

  const {
    data: vehicleAccessesListResponse,
    isSuccess,
    isError,
    isLoading,
    isFetching
  } = useGetWhiteListByBuildingIdQuery(requestParams)
  
  const showSkeleton = isLoading || (isFetching && !vehicleAccessesListResponse?.data)
  const totalCount = Number(vehicleAccessesListResponse?.metadata?.total_count || 0);
  if (totalCount > prefetchSize) { // КОСТЫЛЬ ДЛЯ ТОГО ЧТОБЫ ПОТОМ ВЗЯТЬ ВСЕ ЗАПИСИ
    setPrefetchSize(totalCount)    
  }
  const accessTypeToListMap = useMemo(() => {
    const vehicleAccessesList = vehicleAccessesListResponse?.data ?? [];
    return vehicleAccessesList.reduce((acc, cur) => {
      const normalizedAccessType = normalizeAccessType(cur?.access_type)
      if (normalizedAccessType === 'white') {
        acc.whiteList.push(cur)
      } else if (normalizedAccessType === 'black') {
        acc.blackList.push(cur)
      } else if (normalizedAccessType === 'guest') {
        acc.guestList.push(cur)
      } else {
        acc.otherList.push(cur)
      }
      return acc
    }, {
      'blackList': [],
      'guestList': [],
      'whiteList': [],
      'otherList': [],
    })
  }, [vehicleAccessesListResponse]);

  const selectedVehicleAccessList = accessTypeToListMap[selectedAccessType] ?? [];
  const selectedCount = selectedVehicleAccessList.length;
  
  const accessesListPage = useMemo(() => {
    const startIndex = page * rowsPerPage
    return selectedVehicleAccessList.slice(startIndex, startIndex + rowsPerPage)
  }, [accessTypeToListMap, selectedAccessType, page, rowsPerPage])
  
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }
  
  const getPlaceholderByAccessType = (accessType) => {
    switch (accessType) {
      case 'guestList':
        return `${t('list_empty')}\n${t('guest-list_explained')}`
      case 'whiteList':
        return `${t('list_empty')}\n${t('white-list_explained')}`
      case 'blackList':
        return `${t('list_empty')}\n${t('black-list_explained')}`
      default:
        return t('list_empty')
    }
  } 

  useEffect(() => {
    setPage(0)
  }, [activeBuildingId, search, selectedAccessType, accessTypeToListMap])

  const onDelete = (id) => () => {
    setCurrentId(id)
    setDeleteModal(true)
  }

  const reqDelete = async () => {
    try {
      setDeleteModal(false)
      await handleDelete(currentId).unwrap()
      setCurrentId(null)
    } catch (err) {
      console.log(err)
    }
  }

  const cancelDelete = () => {
    setCurrentId(null)
    setDeleteModal(false)
  }
  
  return (
    <>
      <WidgetPaper
        ref={ref}
        title={t('accesses')}
      >
        <AccessTypeSelector
          value={selectedAccessType}
          onChange={(value) => setSelectedAccessType(value)}
          list={allowedFiltersList}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            mb: 2
          }}>
          <SearchInput 
            placeholder={t('enter_number')}
            onChange={(value) => setSearch(value)}
            delay={300}
          />
          <Divider orientation="vertical" flexItem />
          <ImportExportButton />
          {selectedAccessType && (
            <AddButton
              onClick={() => dispatch(setUpsertModalParams({
                accessType: selectedAccessType.replaceAll('List', ''),
                requestParams,
              }))}
            />
          )}
        </Stack>
        <TableContainer>
          <Table size="small">
            <TableHeader />
            <TableBody>
              {showSkeleton ? (
                <TableRowsSkeleton 
                  rows={rowsPerPage} 
                  columns={accessTableHeaderItems.length} 
                />
              ) : (
                accessesListPage?.map((carAccesses) => (
                  <TableRow key={carAccesses?.id}>
                    <TableCell>
                      <Typography variant="subtitle2" noWrap>
                        {carAccesses?.plate_number}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      {
                        format(
                          new Date(carAccesses.created_at?.replace('Z', '')),
                          'dd.MM.yy / HH:mm'
                        )
                      }
                    </TableCell>
                    <TableCell align="left">
                      {
                        format(
                          new Date(carAccesses.expires_at?.replace('Z', '')),
                          'dd.MM.yy / HH:mm'
                        )
                      }
                    </TableCell>
                    <TableCell align="left">
                      {
                        carAccesses?.is_tg_guest
                          ? t('guest_mode_tg')
                          : carAccesses?.is_guest
                          ? t('guest_mode')
                          : hasAbonementLink(carAccesses)
                          ? t('abonement')
                          : carAccesses?.in_black_list
                          ? t('black')
                          : t('white')
                      }
                    </TableCell>
                    <TableCell component="th" scope="row" padding="none">
                      <Typography variant="subtitle2" noWrap>
                        {carAccesses?.comment}
                      </Typography>
                    </TableCell>
                    <TableCell align="left">
                      {carAccesses?.phone_number || '-'}
                    </TableCell>
                    {
                      hasPermission('access.edit') && (
                        <TableCell align='right'>
                          <Box 
                            sx={{ 
                              display: 'flex', 
                              justifyContent: 'flex-end' 
                            }}
                          >
                            <Tooltip title={t('edit')}>
                              <IconButton
                                size="small"
                                aria-label="edit"
                                color="secondary"
                                sx={{ mr: 2 }}
                                onClick={() => dispatch(setUpsertModalParams({ 
                                  id: carAccesses?.id,
                                  accessType: resolveUiAccessType(carAccesses),
                                  requestParams,
                                }))}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t('delete')}>
                              <IconButton
                                size="small"
                                aria-label="edit"
                                color="error"
                                onClick={onDelete(carAccesses?.id)}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      )
                    }
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {
          selectedVehicleAccessList.length > 0 && !showSkeleton &&
            <TablePageSelector
              count={selectedCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(event, newPage) => setPage(newPage)}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
        }
        {
          !selectedVehicleAccessList.length && isSuccess && !showSkeleton && (
            <TableEmptyPlaceholder
              icon={HowToRegTwoToneIcon}
              message={getPlaceholderByAccessType(selectedAccessType)}
            />
          )
        }
        {
          isError && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <AlertTitle>{t('alert')}</AlertTitle>
              {t('error_occurred')}
            </Alert>
          )
        }
      </WidgetPaper>
      <Dialog open={deleteModal} fullWidth>
        <DialogTitle>{t('delete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('sure_to_delete_item')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>{t('cancel')}</Button>
          <Button color="error" onClick={reqDelete}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Access
